import { useConversationSidePanelContext } from "@app/components/assistant/conversation/ConversationSidePanelContext";
import { useFilePreviewContext } from "@app/components/assistant/conversation/FilePreviewContext";
import { ConversationFileExplorer } from "@app/components/assistant/conversation/files_panel/ConversationFileExplorer";
import { FilesTab } from "@app/components/assistant/conversation/files_panel/FilesTab";
import { SandboxStatusChip } from "@app/components/assistant/conversation/files_panel/SandboxStatusChip";
import { SandboxTab } from "@app/components/assistant/conversation/files_panel/SandboxTab";
import type { ConversationAttachmentItem } from "@app/components/assistant/conversation/files_panel/types";
import { conversationAttachmentToRow } from "@app/components/assistant/conversation/files_panel/utils";
import { AppLayoutTitle } from "@app/components/sparkle/AppLayoutTitle";
import { useConversationAttachments } from "@app/hooks/conversations/useConversationAttachments";
import { useConversationSandboxStatus } from "@app/hooks/conversations/useConversationSandboxStatus";
import { useSendNotification } from "@app/hooks/useNotification";
import { isFileAttachmentType } from "@app/lib/api/assistant/conversation/attachments";
import { downloadFile } from "@app/lib/swr/files";
import type { FileSystemFileEntry } from "@app/types/api/file_system/types";
import type { ConversationWithoutContentType } from "@app/types/assistant/conversation";
import { isFrameContentType } from "@app/types/files";
import type { LightWorkspaceType } from "@app/types/user";
import {
  Button,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  XClose,
} from "@dust-tt/sparkle";
import { useCallback, useMemo, useRef, useState } from "react";

interface ConversationFilesPanelProps {
  conversation: ConversationWithoutContentType;
  owner: LightWorkspaceType;
}

export function ConversationFilesPanel({
  conversation,
  owner,
}: ConversationFilesPanelProps) {
  const isNewFileExplorer = conversation.metadata?.useFileSystem === true;

  const [activeTab, setActiveTab] = useState("files");
  const { openFilePreview } = useFilePreviewContext();
  const isDownloadingRef = useRef(false);
  const blobUrlRef = useRef<string | null>(null);
  const { openPanel, closePanel } = useConversationSidePanelContext();
  const sendNotification = useSendNotification();

  const { attachments, isConversationAttachmentsLoading } =
    useConversationAttachments({
      conversationId: conversation.sId,
      owner,
      options: { disabled: isNewFileExplorer },
    });

  const { sandboxStatus } = useConversationSandboxStatus({
    conversationId: conversation.sId,
    owner,
    options: { disabled: isNewFileExplorer },
  });

  const openFile = useCallback(
    ({
      fileId,
      filePath,
      contentType,
    }: {
      fileId: string;
      filePath?: string | null;
      contentType: string;
    }) => {
      if (isFrameContentType(contentType)) {
        openPanel({ type: "interactive_content", fileId });
      } else {
        openFilePreview({
          fileId,
          filePath: filePath ?? undefined,
          contentType,
        });
      }
    },
    [openPanel, openFilePreview]
  );

  const handleAttachmentClick = useCallback(
    (item: ConversationAttachmentItem) => {
      if (isFileAttachmentType(item)) {
        openFile({
          fileId: item.fileId,
          filePath: item.path,
          contentType: item.contentType,
        });
      }
    },
    [openFile]
  );

  const handleSandboxFileClick = useCallback(
    async (entry: FileSystemFileEntry) => {
      if (entry.fileId) {
        openFile({
          fileId: entry.fileId,
          contentType: entry.contentType,
        });
        return;
      }

      if (isDownloadingRef.current) {
        return;
      }

      // File only exists in GCS, download and open as blob URL.
      isDownloadingRef.current = true;
      try {
        const res = await downloadFile(owner, entry.path);
        const blob = await res.blob();
        if (blobUrlRef.current) {
          URL.revokeObjectURL(blobUrlRef.current);
        }
        const url = URL.createObjectURL(blob);
        blobUrlRef.current = url;

        const a = document.createElement("a");
        a.href = url;
        a.download = entry.fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch {
        sendNotification({
          type: "error",
          title: "Failed to download the file.",
          description: "An error occurred while downloading. Please try again.",
        });
      } finally {
        isDownloadingRef.current = false;
      }
    },
    [openFile, owner, sendNotification]
  );

  const fileRows = useMemo(
    () =>
      attachments
        .filter((a) => !a.hidden)
        .map((a) => conversationAttachmentToRow(a, handleAttachmentClick)),
    [attachments, handleAttachmentClick]
  );

  if (isNewFileExplorer) {
    return (
      <ConversationFileExplorer conversation={conversation} owner={owner} />
    );
  }

  const hasSandbox = sandboxStatus !== null;

  const filesContent = (
    <FilesTab
      isLoading={isConversationAttachmentsLoading}
      owner={owner}
      rows={fileRows}
    />
  );

  if (!hasSandbox) {
    return (
      <>
        <div className="flex h-panel flex-col">
          <AppLayoutTitle>
            <div className="flex h-full items-center justify-between">
              <span className="text-sm font-semibold text-foreground">
                Working Files
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={closePanel}
                icon={XClose}
              />
            </div>
          </AppLayoutTitle>
          {filesContent}
        </div>
      </>
    );
  }

  return (
    <>
      <div className="flex h-panel flex-col">
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="flex h-full flex-col"
        >
          <AppLayoutTitle>
            <div className="flex h-full items-center justify-between">
              <TabsList border={false}>
                <TabsTrigger value="files" label="Working Files" />
                <TabsTrigger value="sandbox" label="Mounted Files" />
              </TabsList>
              <div className="flex items-center gap-2">
                {sandboxStatus && <SandboxStatusChip status={sandboxStatus} />}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={closePanel}
                  icon={XClose}
                />
              </div>
            </div>
          </AppLayoutTitle>
          <TabsContent value="files" className="flex-1 overflow-hidden">
            {filesContent}
          </TabsContent>
          <TabsContent value="sandbox" className="flex-1 overflow-hidden">
            <SandboxTab
              conversationId={conversation.sId}
              disabled={activeTab !== "sandbox"}
              owner={owner}
              onFileClick={handleSandboxFileClick}
            />
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}
